import {pokpokTheme} from '@pokpok/astryx';
import {accentOverrideCss} from './accent';
import {surfaceTokensCss} from './surfaceTokens';

/**
 * The pokpok theme's custom properties, for one surface: light.
 *
 * Emitted as a single `<style>` rather than a build-time stylesheet because
 * `pokpokTheme` is the source of truth and lives in `@pokpok/astryx` — and a
 * cross-repo test there already asserts those colours match
 * `pokpok_tokens/palette.dart`, so the till and the web page cannot drift into
 * two different reds. Transcribing hex values into a stylesheet here would
 * escape that check, which is exactly the drift this project has already paid
 * for once.
 *
 * # Light only, and that is a decision rather than an omission
 *
 * Until 2026-08-13 this file emitted both schemes and the page followed
 * `prefers-color-scheme`. The owner dropped the dark scheme for the guest QR
 * surface on that date, with the redesign: the design package is drawn on Sticky
 * Rice `#F7F3EE` throughout, every one of its thirteen screens, and there is no
 * dark drawing of any of them. A `prefers-color-scheme` block emitting
 * `pokpokTheme.css('dark')` would not have produced "the design, darker" — it
 * would have produced an undesigned page for the half of guests whose phone is
 * set to dark, and nobody would have seen it, because a reviewer's phone is set
 * however it is set.
 *
 * `color-scheme: light` — not `light dark` — is the load-bearing half of that.
 * It is what stops the browser painting its own form controls, scrollbars, text
 * selection and (on Android) the autofill dropdown in dark, which is what leaves
 * a black `<select>` on a Sticky Rice page. Declaring the scheme is also what
 * suppresses Chrome's and Samsung Internet's forced auto-darkening.
 *
 * **`pokpokTheme` keeps both halves of every token pair.** Nothing was stripped
 * from astryx: the till and the KDS still render dark, `tokensAgreeWithFlutter`
 * still checks Chili Glow, and the day this surface wants dark it is one `@media`
 * block here again. This file consumes one scheme; it does not delete one.
 *
 * # This file cannot paint the first frame, and that is why index.html does
 *
 * Everything here is emitted **by React, at runtime**. So until the bundle has
 * landed, parsed and executed, no brand token exists on the page at all —
 * including `--color-background-body`, which `global.css` sets the body
 * background from. Until 2026-08-13 the served HTML was `<div id="root">` and
 * nothing else, and the consequence was a blank white viewport for the whole
 * length of a ~110 KB gzipped critical path, on the one surface whose hardware
 * and network the merchant does not own — a guest's own ฿5,000 phone on
 * dining-room 4G.
 *
 * `index.html` now carries a small inline `<style>` that defines three of these
 * tokens (`--color-background-body`, `--color-text-primary`,
 * `--color-skeleton`) and paints a ground plus a skeleton. That block does not
 * replace this one and is not a second source of truth: it is a duplicate of
 * three values, deliberately, for the ~200ms this file cannot cover. It was six
 * values until the dark scheme went; the three that remain are the light ones.
 *
 * ## Why those three values are typed by hand there
 *
 * The obvious fix is a Vite `transformIndexHtml` plugin that reads
 * `pokpokTheme` and injects the block, so the numbers exist once. **It cannot
 * be built.** Vite loads `vite.config.ts` through Node's own ESM loader, and
 * `@pokpok/astryx` ships TypeScript source whose internal imports carry no file
 * extensions — so `import {pokpokTheme} from '@pokpok/astryx'` inside the
 * config throws `ERR_MODULE_NOT_FOUND` before the build starts. That package is
 * reachable from inside a bundler and nowhere else. (Tried, 2026-08-13. If
 * astryx ever gains a build step or extensioned imports, this becomes possible
 * and is the better answer.)
 *
 * So the duplication was moved somewhere it can be checked rather than removed:
 * `test/guards.test.ts` asserts every colour literal in `index.html` — the three
 * in that block and the one `theme-color` meta — still equals what
 * `pokpokTheme.variables('light')` returns, and that the file carries no hex the
 * theme does not own. The drift this prevents is quiet by nature: a brand colour
 * changed here, the first 200ms of every scan painting last season's ground, and
 * nothing in any screenshot review able to catch it.
 *
 * # The third block: tokens astryx does not carry
 *
 * `surfaceTokensCss()` appends the handful of custom properties this surface
 * needs and the shared theme has no value for — the warm hairline above all.
 * They are emitted here, after the theme, so a screen never has to know which
 * layer a token came from: everything is `var(--…)` on `:root` either way. The
 * justification for each one, and the reason it is not in astryx, is in
 * `surfaceTokens.ts` — read that before adding a fourth.
 *
 * ## What the inline block deliberately does not do
 *
 * It does not render the venue name. That comes from `GuestSession`, a network
 * round trip away, and there is no SSR on this surface to wait for it — so
 * venue identity stays Q1's job. Scope creep here would mean building a server
 * renderer to save one string.
 */
export function themeCss(): string {
  return [
    ':root {',
    '  color-scheme: light;',
    pokpokTheme.css('light'),
    surfaceTokensCss(),
    '}',
  ].join('\n');
}

/**
 * Render once, at the root, above everything.
 *
 * `dangerouslySetInnerHTML` is the intended way to put generated CSS into a
 * `<style>` — the content is a string this application produced from its own
 * design tokens, not anything a guest or a merchant can influence.
 */
export function ThemeStyles() {
  return <style dangerouslySetInnerHTML={{__html: themeCss()}} />;
}

/**
 * The merchant accent, layered over the theme.
 *
 * A separate `<style>` from `ThemeStyles` on purpose: this one carries per-tenant
 * data that arrives over the network (see `accent.ts`), so it cannot live in the
 * theme block that a cross-repo test holds to the palette. It is mounted **below**
 * `ThemeStyles` in the tree, so its `:root {}` block comes later in the document
 * and wins at equal specificity — overriding exactly the accent tokens.
 *
 * Renders nothing when there is no accent or the value is not a colour, which is
 * the ordinary case: a shop with no branding keeps Chili Red and this component is
 * inert.
 */
export function AccentStyles({accentColor}: {accentColor: string | null | undefined}) {
  const css = accentOverrideCss(accentColor);
  if (!css) return null;
  return <style dangerouslySetInnerHTML={{__html: css}} />;
}
